import {
  OrderSummary,
  Typography,
  useWidgetReadyAfterInitialLoad,
  WidgetReadyBoundary,
} from '@components'
import { useOrderByCode } from '@haus-storefront-react/hooks'
import type { OrderSummaryConfigStyles } from '@components/order-summary'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import './OrderConfirmationSkeleton.css'
import { OrderConfirmationSkeleton } from './skeleton'
import styles from './OrderConfirmation.module.scss'

interface OrderConfirmationWidgetProps {
  configStyles?: OrderSummaryConfigStyles
}

/**
 * Resolve order code from `?orderCode=...` query parameter.
 */
const getOrderCodeFromUrl = (): string => {
  const searchParams = new URLSearchParams(window.location.search)
  const value = searchParams.get('orderCode')
  return value && value.trim().length > 0 ? value.trim() : ''
}

/**
 * Render order confirmation details for the `orderCode` in the URL query string.
 */
export const OrderConfirmationWidget = ({ configStyles }: OrderConfirmationWidgetProps) => {
  const orderCode = useMemo(() => getOrderCodeFromUrl(), [])
  const { t } = useTranslation()
  const { data: order, isLoading, error } = useOrderByCode(orderCode)
  const hasSettledContent = Boolean(order) || Boolean(error) || (!isLoading && !order)
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent,
    hasVisibleContent: true,
    isLoading,
  })

  if (!orderCode) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <section className={styles.root}>
          <div className={styles.error} role="alert">
            <Typography variant="body">{t('orderConfirmation.noOrderCode')}</Typography>
          </div>
        </section>
      </WidgetReadyBoundary>
    )
  }

  if (isLoading) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <section className={styles.root}>
          <OrderConfirmationSkeleton />
        </section>
      </WidgetReadyBoundary>
    )
  }

  if (error) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <section className={styles.root}>
          <div className={styles.error} role="alert">
            <Typography variant="body">{t('orderConfirmation.error')}</Typography>
          </div>
        </section>
      </WidgetReadyBoundary>
    )
  }

  if (!order) {
    return (
      <WidgetReadyBoundary isReady={isReady}>
        <section className={styles.root}>
          <div role="status" aria-live="polite">
            <Typography variant="body">{t('orderConfirmation.noOrderFound')}</Typography>
          </div>
        </section>
      </WidgetReadyBoundary>
    )
  }

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <section className={styles.root} aria-label={t('orderConfirmation.orderCode')}>
        <div className={styles.summarySection}>
          <OrderSummary
            order={order}
            renderCta={false}
            renderProductsToggle={false}
            configStyles={configStyles}
          />
        </div>
      </section>
    </WidgetReadyBoundary>
  )
}

export default OrderConfirmationWidget
